/**
 * Completes email verification (User Onboarding + Module Access -- Pilot).
 * Reads `?token=` from the URL (same pattern as ResetPasswordPage) and
 * calls it immediately on mount -- there is no form, just a status display.
 */
import { useEffect, useRef, useState } from "react";
import { Link as RouterLink, useSearchParams } from "react-router-dom";
import { Alert, CircularProgress, Link, Stack, Typography } from "@mui/material";
import authService from "@/services/authService";
import { extractErrorMessage } from "@/utils/errors";

type VerifyState = "verifying" | "success" | "error";

export function VerifyEmailPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") ?? "";
  const [state, setState] = useState<VerifyState>(token ? "verifying" : "error");
  const [message, setMessage] = useState<string | null>(null);
  const attempted = useRef(false);

  useEffect(() => {
    if (!token || attempted.current) return;
    attempted.current = true;
    authService
      .verifyEmail(token)
      .then(() => setState("success"))
      .catch((error: unknown) => {
        setMessage(extractErrorMessage(error));
        setState("error");
      });
  }, [token]);

  if (!token) {
    return (
      <Alert severity="error">
        This verification link is missing a token. Please request a new one.
      </Alert>
    );
  }

  if (state === "verifying") {
    return (
      <Stack spacing={2} alignItems="center">
        <CircularProgress size={28} />
        <Typography variant="body2" color="text.secondary">
          Verifying your email...
        </Typography>
      </Stack>
    );
  }

  if (state === "success") {
    return (
      <Stack spacing={2}>
        <Alert severity="success">Email verified successfully. You can now log in.</Alert>
        <Link component={RouterLink} to="/login" variant="body2" textAlign="center">
          Go to sign in
        </Link>
      </Stack>
    );
  }

  return (
    <Stack spacing={2}>
      <Alert severity="error">{message ?? "This verification link is invalid or has expired."}</Alert>
      <Link component={RouterLink} to="/resend-verification" variant="body2" textAlign="center">
        Resend verification email
      </Link>
      <Link component={RouterLink} to="/login" variant="body2" textAlign="center">
        Back to sign in
      </Link>
    </Stack>
  );
}
export default VerifyEmailPage;
